import { useState, useEffect, useRef, useMemo } from "react"
import {
  Milestone,
  MilestoneInput,
  MilestoneStatus,
  MilestoneTodo,
  MilestoneAssignee,
  STATUS_META,
  KANBAN_COLUMNS,
  dateInputToIso,
  isoToDateInput,
  createTodoId,
  getAssignees,
  getTodoProgress,
} from "../hooks/useMilestones"
import { useSelectableUsers } from "../hooks/useEquipmentNotify"

interface ProjectOption {
  _id: string
  projectName: string
}

interface MilestoneFormModalProps {
  open: boolean
  editTarget: Milestone | null
  projects: ProjectOption[]
  defaultProjectId?: string
  onClose: () => void
  onSubmit: (input: MilestoneInput) => Promise<void>
}

export function MilestoneFormModal({
  open,
  editTarget,
  projects,
  defaultProjectId,
  onClose,
  onSubmit,
}: MilestoneFormModalProps) {
  const [projectId, setProjectId] = useState("")
  const [title, setTitle] = useState("")
  const [deliverable, setDeliverable] = useState("")
  const [description, setDescription] = useState("")
  const [startDate, setStartDate] = useState("")
  const [dueDate, setDueDate] = useState("")
  const [slackDays, setSlackDays] = useState("0")
  const [status, setStatus] = useState<MilestoneStatus>("todo")
  const [progress, setProgress] = useState("0")
  const [assignees, setAssignees] = useState<MilestoneAssignee[]>([])
  const [assigneeSearch, setAssigneeSearch] = useState("")
  const [todos, setTodos] = useState<MilestoneTodo[]>([])
  const [todoDraft, setTodoDraft] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")
  const { users, loaded: usersLoaded } = useSelectableUsers()
  // 오버레이에서 mousedown이 시작된 경우에만 닫기 (드래그 중 오동작 방지)
  const overlayMouseDown = useRef(false)

  // 모달 열릴 때 폼 초기화
  useEffect(() => {
    if (!open) return
    if (editTarget) {
      setProjectId(editTarget.projectId || "")
      setTitle(editTarget.title || "")
      setDeliverable(editTarget.deliverable || "")
      setDescription(editTarget.description || "")
      setStartDate(isoToDateInput(editTarget.startDate))
      setDueDate(isoToDateInput(editTarget.dueDate))
      setSlackDays(String(editTarget.slackDays ?? 0))
      setStatus(editTarget.status || "todo")
      setProgress(String(editTarget.progress ?? 0))
      setAssignees(getAssignees(editTarget))
      setTodos(editTarget.todos || [])
    } else {
      const today = new Date().toISOString().slice(0, 10)
      setProjectId(defaultProjectId || "")
      setTitle("")
      setDeliverable("")
      setDescription("")
      setStartDate(today)
      setDueDate(today)
      setSlackDays("0")
      setStatus("todo")
      setProgress("0")
      setAssignees([])
      setTodos([])
    }
    setAssigneeSearch("")
    setTodoDraft("")
    setError("")
    setSubmitting(false)
  }, [open, editTarget, defaultProjectId])

  // ESC 키로 닫기
  useEffect(() => {
    if (!open) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", handleKey)
    return () => window.removeEventListener("keydown", handleKey)
  }, [open, onClose])

  const selectedIds = new Set(assignees.map((a) => a.userId))

  // 검색어 기준 후보 필터링 (이미 선택된 사용자는 제외)
  const candidates = useMemo(() => {
    const keyword = assigneeSearch.trim().toLowerCase()
    return users
      .filter((u) => !selectedIds.has(u.userId))
      .filter((u) => {
        if (!keyword) return true
        return (
          (u.userName || "").toLowerCase().includes(keyword) ||
          (u.teamName || "").toLowerCase().includes(keyword) ||
          (u.email || "").toLowerCase().includes(keyword)
        )
      })
      .slice(0, 40)
  }, [users, assigneeSearch, assignees])

  if (!open) return null

  const toggleAssignee = (user: { userId: string; userName: string; email?: string; teamName?: string }) => {
    setAssignees((prev) => {
      if (prev.some((a) => a.userId === user.userId)) {
        return prev.filter((a) => a.userId !== user.userId)
      }
      return [
        ...prev,
        {
          userId: user.userId,
          userName: user.userName,
          email: user.email || "",
          teamName: user.teamName || "",
        },
      ]
    })
  }

  const removeAssignee = (userId: string) => {
    setAssignees((prev) => prev.filter((a) => a.userId !== userId))
  }

  const addTodo = () => {
    const text = todoDraft.trim()
    if (!text) return
    setTodos((prev) => [...prev, { id: createTodoId(), text, done: false }])
    setTodoDraft("")
  }

  const removeTodo = (id: string) => {
    setTodos((prev) => prev.filter((t) => t.id !== id))
  }

  const toggleDraftTodo = (id: string) => {
    setTodos((prev) =>
      prev.map((t) =>
        t.id === id
          ? { ...t, done: !t.done, doneAt: !t.done ? new Date().toISOString() : "" }
          : t
      )
    )
  }

  const updateTodoText = (id: string, text: string) => {
    setTodos((prev) => prev.map((t) => (t.id === id ? { ...t, text } : t)))
  }

  const doneCount = todos.filter((t) => t.done).length
  // 세부 To do가 있으면 진행률은 체크리스트 완료율로 자동 계산된다
  const todoProgress = getTodoProgress(todos)
  const autoProgress = todoProgress !== null
  const effectiveProgress = autoProgress ? String(todoProgress) : progress

  const handleSubmit = async () => {
    if (submitting) return
    if (!projectId) {
      setError("프로젝트를 선택해주세요.")
      return
    }
    if (!title.trim()) {
      setError("마일스톤 이름을 입력해주세요.")
      return
    }
    if (!startDate || !dueDate) {
      setError("시작일과 목표 완료일을 입력해주세요.")
      return
    }
    if (new Date(dueDate) < new Date(startDate)) {
      setError("목표 완료일은 시작일보다 빠를 수 없습니다.")
      return
    }

    setSubmitting(true)
    setError("")
    try {
      const project = projects.find((p) => p._id === projectId)
      await onSubmit({
        projectId,
        projectName: project?.projectName || "",
        title: title.trim(),
        deliverable: deliverable.trim(),
        description: description.trim(),
        todos: todos
          .map((t) => ({ ...t, text: t.text.trim() }))
          .filter((t) => t.text.length > 0),
        startDate: dateInputToIso(startDate),
        dueDate: dateInputToIso(dueDate),
        slackDays: Math.max(0, parseInt(slackDays, 10) || 0),
        status,
        progress: Math.min(100, Math.max(0, parseInt(effectiveProgress, 10) || 0)),
        assignees,
      })
      onClose()
    } catch (err) {
      console.error("마일스톤 저장 실패:", err)
      setError("저장에 실패했습니다. 다시 시도해주세요.")
      setSubmitting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onMouseDown={(e) => {
        overlayMouseDown.current = e.target === e.currentTarget
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && overlayMouseDown.current) onClose()
        overlayMouseDown.current = false
      }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* 헤더 */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h3 className="text-lg font-bold text-gray-900">
            {editTarget ? "마일스톤 수정" : "마일스톤 추가"}
          </h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="닫기"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 본문 */}
        <div className="p-6 space-y-5">
          {/* 프로젝트 */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              프로젝트 <span className="text-red-500">*</span>
            </label>
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
            >
              <option value="">프로젝트 선택</option>
              {projects.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.projectName}
                </option>
              ))}
            </select>
          </div>

          {/* 마일스톤 이름 */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              마일스톤 이름 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="예: 상세 설계 완료, 시제품 조립"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
            />
          </div>

          {/* 산출물 */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">산출물</label>
            <input
              type="text"
              value={deliverable}
              onChange={(e) => setDeliverable(e.target.value)}
              placeholder="예: 2D/3D 도면, 시험성적서, 사용자 매뉴얼"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
            />
            <p className="mt-1 text-xs text-gray-500">해당 마일스톤 완료 시 제출되는 결과물을 기입합니다.</p>
          </div>

          {/* 일정 */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                시작일 <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                목표 완료일 <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                여유(Slack) 일수
              </label>
              <input
                type="number"
                min={0}
                value={slackDays}
                onChange={(e) => setSlackDays(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              />
              <p className="mt-1 text-xs text-gray-500">예상 지연 버퍼</p>
            </div>
          </div>

          {/* 상태 / 진행률 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">상태</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as MilestoneStatus)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              >
                {KANBAN_COLUMNS.map((s) => (
                  <option key={s} value={s}>
                    {STATUS_META[s].label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">진행률 (%)</label>
              <input
                type="number"
                min={0}
                max={100}
                value={effectiveProgress}
                onChange={(e) => setProgress(e.target.value)}
                disabled={autoProgress}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
              />
              {autoProgress ? (
                <p className="mt-1 text-xs text-orange-600">
                  세부 To do {doneCount}/{todos.length} 완료 기준으로 자동 계산됩니다.
                </p>
              ) : (
                <p className="mt-1 text-xs text-gray-500">세부 To do 등록 시 자동 계산됩니다.</p>
              )}
            </div>
          </div>

          {/* 담당자 (활성 사용자 복수 선택) */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-semibold text-gray-700">담당자</label>
              <span className="text-xs font-medium text-gray-500">
                {assignees.length > 0 ? `${assignees.length}명 선택됨` : "복수 선택 가능"}
              </span>
            </div>

            {/* 선택된 담당자 칩 */}
            {assignees.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {assignees.map((a) => (
                  <span
                    key={a.userId || a.userName}
                    className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full bg-orange-50 border border-orange-200 text-xs font-medium text-orange-800"
                  >
                    {a.userName}
                    {a.teamName ? <span className="text-orange-500">· {a.teamName}</span> : null}
                    <button
                      type="button"
                      onClick={() => removeAssignee(a.userId)}
                      aria-label={`${a.userName} 제외`}
                      className="p-0.5 rounded-full text-orange-500 hover:text-white hover:bg-orange-500 transition-colors"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            ) : null}

            {/* 검색 */}
            <input
              type="text"
              value={assigneeSearch}
              onChange={(e) => setAssigneeSearch(e.target.value)}
              placeholder="이름 또는 팀으로 검색"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
            />

            {/* 후보 목록 */}
            <div className="mt-2 max-h-44 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
              {!usersLoaded ? (
                <p className="px-3 py-3 text-sm text-gray-500">사용자 목록을 불러오는 중...</p>
              ) : candidates.length === 0 ? (
                <p className="px-3 py-3 text-sm text-gray-500">
                  {assigneeSearch.trim() ? "검색 결과가 없습니다." : "선택 가능한 활성 사용자가 없습니다."}
                </p>
              ) : (
                candidates.map((u) => (
                  <button
                    key={u.userId}
                    type="button"
                    onClick={() => toggleAssignee(u)}
                    className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-orange-50 transition-colors"
                  >
                    <span className="text-sm text-gray-800">{u.userName}</span>
                    <span className="text-xs text-gray-400">{u.teamName || u.email}</span>
                  </button>
                ))
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              등록된 활성 사용자 중에서 선택합니다. 선택된 담당자는 개인 To do 목록에도 표시됩니다.
            </p>
          </div>

          {/* 상세 설명 */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">상세 설명</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="마일스톤 범위, 완료 조건 등"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none resize-y"
            />
          </div>

          {/* 세부 To do 목록 */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-semibold text-gray-700">세부 To do 목록</label>
              {todos.length > 0 ? (
                <span className="text-xs font-medium text-gray-500">
                  {doneCount} / {todos.length} 완료
                </span>
              ) : null}
            </div>

            {/* 입력 */}
            <div className="flex gap-2">
              <input
                type="text"
                value={todoDraft}
                onChange={(e) => setTodoDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    addTodo()
                  }
                }}
                placeholder="예: 부품 발주서 작성 (Enter로 추가)"
                className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              />
              <button
                type="button"
                onClick={addTodo}
                disabled={!todoDraft.trim()}
                className="px-4 py-2.5 rounded-lg font-medium text-white bg-orange-600 hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
              >
                추가
              </button>
            </div>

            {/* 목록 */}
            {todos.length > 0 ? (
              <ul className="mt-3 space-y-1.5">
                {todos.map((t) => (
                  <li
                    key={t.id}
                    className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50"
                  >
                    <button
                      type="button"
                      onClick={() => toggleDraftTodo(t.id)}
                      aria-label={t.done ? "완료 해제" : "완료 처리"}
                      className={`w-5 h-5 shrink-0 rounded border flex items-center justify-center transition-colors ${
                        t.done
                          ? "bg-emerald-500 border-emerald-500 text-white"
                          : "bg-white border-gray-300 hover:border-orange-400"
                      }`}
                    >
                      {t.done ? (
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : null}
                    </button>
                    <input
                      type="text"
                      value={t.text}
                      onChange={(e) => updateTodoText(t.id, e.target.value)}
                      className={`flex-1 bg-transparent text-sm outline-none ${
                        t.done ? "line-through text-gray-400" : "text-gray-800"
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => removeTodo(t.id)}
                      aria-label="항목 삭제"
                      className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-xs text-gray-500">
                마일스톤 안에서 처리할 세부 작업을 등록하면, 일정관리·To do 목록에서 바로 체크할 수 있습니다.
              </p>
            )}
          </div>

          {error ? (
            <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          ) : null}
        </div>

        {/* 푸터 */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-lg font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-100 transition-colors"
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-5 py-2.5 rounded-lg font-semibold text-white bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? "저장 중..." : editTarget ? "수정 저장" : "추가"}
          </button>
        </div>
      </div>
    </div>
  )
}
